import { useEffect, useState } from 'react';

/**
 * Hook to detect if navbar items are overflowing their container
 * @param {string} containerId - The ID of the navbar container element
 * @param {string} itemsClass - The class name of the navbar items
 * @returns {boolean} - True if items are overflowing, false otherwise
 */
export const useNavbarOverflow = (containerId, itemsClass) => {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const container = document.getElementById(containerId);
      const items = container?.querySelector(`.${itemsClass}`);
      
      if (container && items) {
        const containerWidth = container.offsetWidth;
        const itemsWidth = items.scrollWidth;
        setIsOverflowing(itemsWidth > containerWidth);
      }
    };

    // Check on mount and window resize
    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    // Cleanup
    return () => window.removeEventListener('resize', checkOverflow);
  }, [containerId, itemsClass]);

  return isOverflowing;
};

/**
 * Function to calculate if navbar items will overflow based on their content
 * @param {HTMLElement} container - The navbar container element
 * @param {HTMLElement[]} items - Array of navbar item elements
 * @returns {boolean} - True if items will overflow, false otherwise
 */
export const willNavbarOverflow = (container, items) => {
  if (!container || !items?.length) return false;

  const containerWidth = container.offsetWidth;
  const itemsWidth = items.reduce((total, item) => total + item.offsetWidth, 0);
  const spacing = items.length * 32; // Assuming 2rem (32px) spacing between items

  return itemsWidth + spacing > containerWidth;
};

/**
 * Function to get the optimal number of items that can fit in the navbar
 * @param {HTMLElement} container - The navbar container element
 * @param {HTMLElement[]} items - Array of navbar item elements
 * @returns {number} - Number of items that can fit
 */
export const getOptimalNavbarItems = (container, items) => {
  if (!container || !items?.length) return 0;

  const containerWidth = container.offsetWidth;
  let currentWidth = 0;
  let count = 0;

  for (const item of items) {
    const itemWidth = item.offsetWidth;
    if (currentWidth + itemWidth + 32 <= containerWidth) { // 32px for spacing
      currentWidth += itemWidth + 32;
      count++;
    } else {
      break;
    }
  }

  return count;
}; 